import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import { GlobalSidebar } from './components/GlobalSidebar';
import { TabBar } from './components/TabBar';
import HomeTab from './pages/HomeTab';
import CampaignTab from './pages/CampaignTab';
import Dashboard from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import AgentStudio from './pages/AgentStudio';
import BrandingPage from './pages/BrandingPage';
import ContactLists from './pages/ContactLists';
import FormBuilder from './pages/FormBuilder';
import Analytics from './pages/Analytics';
import Leads from './pages/Leads';
import Settings from './pages/Settings';
import ContentLibrary from './pages/ContentLibrary';
import DeliverabilityPage from './pages/DeliverabilityPage';
import WorkflowBuilder from './pages/WorkflowBuilder';
import { loginUser, setAuthToken, getCurrentUser, createCampaign } from './lib/api';
import { toast } from 'sonner';
import './App.css';

// ─── Login Page ─────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await loginUser({ email, password });
      const token = res.data?.token;
      if (token) {
        setAuthToken(token);
        onLogin(token);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
      <div className="w-full max-w-sm p-6 rounded-xl border border-border/50 bg-[hsl(var(--card))]">
        <h1 className="text-xl font-bold text-foreground mb-1" data-testid="login-title">CipherCampaign</h1>
        <p className="text-sm text-muted-foreground mb-5">Sign in to your workspace</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="email"
            data-testid="login-email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-border bg-[hsl(var(--background))] text-foreground text-sm outline-none focus:ring-2 ring-[hsl(var(--accent)/0.3)]"
            required
          />
          <input
            type="password"
            data-testid="login-password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-border bg-[hsl(var(--background))] text-foreground text-sm outline-none focus:ring-2 ring-[hsl(var(--accent)/0.3)]"
            required
          />
          {error && <p className="text-destructive text-xs">{error}</p>}
          <button
            type="submit"
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full h-10 rounded-lg bg-[hsl(var(--accent))] text-white font-medium text-sm hover:opacity-90 transition-opacity duration-150 disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── App Shell (Sidebar + Main Column) ─────────────────────────────
function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();

  // Campaign tab state (lives at shell level so sidebar + pages can interact)
  const [campaignTabs, setCampaignTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('home');

  // Determine if we're on the workspace (home/campaigns/campaign-builder) vs settings pages
  const isWorkspacePath = location.pathname === '/' || location.pathname.startsWith('/campaign-builder');

  // ─── Create new campaign ───
  const handleCreateCampaign = useCallback(async (prompt, materialType) => {
    try {
      const res = await createCampaign({ topic: prompt, material_type: materialType || 'urgent_alert' });
      const newId = res.data?.id;
      if (!newId) throw new Error('No campaign ID returned');
      const newTab = {
        id: newId,
        name: prompt.length > 40 ? prompt.slice(0, 40) + '...' : prompt,
        initialPrompt: prompt,
      };
      setCampaignTabs(prev => [...prev, newTab]);
      setActiveTab(newId);
      // Navigate to workspace if on a settings page
      if (!isWorkspacePath) navigate('/');
      toast.success('Campaign created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create campaign');
    }
  }, [isWorkspacePath, navigate]);

  // ─── Open existing campaign ───
  const handleOpenCampaign = useCallback((campaign) => {
    const existing = campaignTabs.find(t => t.id === campaign.id);
    if (existing) {
      setActiveTab(campaign.id);
      if (!isWorkspacePath) navigate('/');
      return;
    }
    const topic = campaign.topic || campaign.name || 'Untitled';
    const newTab = {
      id: campaign.id,
      name: topic.length > 40 ? topic.slice(0, 40) + '...' : topic,
      initialPrompt: null,
    };
    setCampaignTabs(prev => [...prev, newTab]);
    setActiveTab(campaign.id);
    if (!isWorkspacePath) navigate('/');
  }, [campaignTabs, isWorkspacePath, navigate]);

  // ─── Close tab ───
  const handleCloseTab = useCallback((tabId) => {
    setCampaignTabs(prev => prev.filter(t => t.id !== tabId));
    if (activeTab === tabId) setActiveTab('home');
  }, [activeTab]);

  // ─── New campaign from sidebar ───
  const handleSidebarNewCampaign = useCallback(() => {
    setActiveTab('home');
    if (!isWorkspacePath) navigate('/');
  }, [isWorkspacePath, navigate]);

  return (
    <div className="flex min-h-screen bg-[hsl(var(--background))]" data-testid="app-shell">
      {/* Global Sidebar - always visible */}
      <GlobalSidebar onNewCampaign={handleSidebarNewCampaign} onCreateCampaign={handleCreateCampaign} />

      {/* Main content area */}
      <main className="ml-[220px] flex-1 flex flex-col min-w-0 h-screen overflow-hidden">
        {/* Workspace view (with TabBar + campaign tabs) */}
        {isWorkspacePath && (
          <>
            <TabBar
              tabs={campaignTabs}
              activeTab={activeTab}
              onSelectTab={setActiveTab}
              onCloseTab={handleCloseTab}
              onNewTab={() => setActiveTab('home')}
            />
            <div className="flex-1 overflow-hidden">
              {activeTab === 'home' ? (
                <HomeTab
                  onCreateCampaign={handleCreateCampaign}
                  onOpenCampaign={handleOpenCampaign}
                />
              ) : (
                <CampaignTab
                  key={activeTab}
                  campaignId={activeTab}
                  initialPrompt={campaignTabs.find(t => t.id === activeTab)?.initialPrompt}
                />
              )}
            </div>
          </>
        )}

        {/* Settings pages (no TabBar) */}
        {!isWorkspacePath && (
          <div className="flex-1 overflow-auto">
            <Routes>
              <Route path="/campaigns" element={<Campaigns onOpenCampaign={handleOpenCampaign} />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/agents" element={<AgentStudio />} />
              <Route path="/branding" element={<BrandingPage />} />
              <Route path="/contacts" element={<ContactLists />} />
              <Route path="/forms" element={<FormBuilder />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/leads" element={<Leads />} />
              <Route path="/content-library" element={<ContentLibrary />} />
              <Route path="/deliverability" element={<DeliverabilityPage />} />
              <Route path="/workflow-builder" element={<WorkflowBuilder />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
        )}
      </main>
    </div>
  );
}

// ─── App Root ───────────────────────────────────────────────────────
function AppRoutes() {
  const [authed, setAuthed] = useState(!!localStorage.getItem('cc_token'));

  useEffect(() => {
    const token = localStorage.getItem('cc_token');
    if (token) {
      setAuthToken(token);
      getCurrentUser().catch(() => {
        setAuthToken(null);
        setAuthed(false);
      });
    }
  }, []);

  const handleLogin = () => setAuthed(true);

  if (!authed) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <AppShell />;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
